/**
 * Byte / string conversions.
 *
 * Deliberately dependency-free (no Buffer, no atob/btoa): this module runs in the browser,
 * in jsdom under Jest, and must behave identically in both. Everything here is strict —
 * a malformed hex or base64 string throws instead of silently producing garbage bytes,
 * because "silently wrong bytes" in a signing path is how funds get lost.
 *
 * Note on the CRA build: `target` is ES5 here, so never spread iterators
 * (`[...map.entries()]`, `[...bytes]`) — use `Array.from` / index loops.
 */

import { InvalidInputError } from './errors';

const HEX_ALPHABET = '0123456789abcdef';
const BASE64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

const BASE64_LOOKUP: { [char: string]: number } = {};
for (let i = 0; i < BASE64_ALPHABET.length; i += 1) {
  BASE64_LOOKUP[BASE64_ALPHABET.charAt(i)] = i;
}
// URL-safe aliases, accepted on decode only.
BASE64_LOOKUP['-'] = 62;
BASE64_LOOKUP['_'] = 63;

/** Lowercase hex encoding. */
export function toHex(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 1) {
    const b = bytes[i];
    out += HEX_ALPHABET.charAt((b >>> 4) & 0x0f) + HEX_ALPHABET.charAt(b & 0x0f);
  }
  return out;
}

/**
 * Strict hex decoding. Accepts an optional `0x` prefix and either case.
 * @param expectedLength when given, the decoded byte length must match exactly.
 */
export function fromHex(hex: string, expectedLength?: number): Uint8Array {
  if (typeof hex !== 'string') throw new InvalidInputError('Expected a hex string');
  const clean = hex.trim().replace(/^0x/i, '');
  if (clean.length === 0) throw new InvalidInputError('Empty hex string');
  if (clean.length % 2 !== 0) throw new InvalidInputError('Hex string has an odd number of characters');
  if (!/^[0-9a-fA-F]+$/.test(clean)) throw new InvalidInputError('Hex string contains non-hex characters');

  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i += 1) {
    out[i] = parseInt(clean.substr(i * 2, 2), 16);
  }
  if (expectedLength !== undefined && out.length !== expectedLength) {
    throw new InvalidInputError(`Expected ${expectedLength} bytes, got ${out.length}`);
  }
  return out;
}

/** Standard base64 (with `=` padding). */
export function toBase64(bytes: Uint8Array): string {
  let out = '';
  let i = 0;
  for (; i + 2 < bytes.length; i += 3) {
    const n = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];
    out +=
      BASE64_ALPHABET.charAt((n >>> 18) & 63) +
      BASE64_ALPHABET.charAt((n >>> 12) & 63) +
      BASE64_ALPHABET.charAt((n >>> 6) & 63) +
      BASE64_ALPHABET.charAt(n & 63);
  }
  const rest = bytes.length - i;
  if (rest === 1) {
    const n = bytes[i] << 16;
    out += BASE64_ALPHABET.charAt((n >>> 18) & 63) + BASE64_ALPHABET.charAt((n >>> 12) & 63) + '==';
  } else if (rest === 2) {
    const n = (bytes[i] << 16) | (bytes[i + 1] << 8);
    out +=
      BASE64_ALPHABET.charAt((n >>> 18) & 63) +
      BASE64_ALPHABET.charAt((n >>> 12) & 63) +
      BASE64_ALPHABET.charAt((n >>> 6) & 63) +
      '=';
  }
  return out;
}

/**
 * Strict base64 decoding. Accepts standard and URL-safe alphabets, with or without padding.
 * Rejects any character outside the alphabet (including whitespace).
 */
export function fromBase64(value: string, expectedLength?: number): Uint8Array {
  if (typeof value !== 'string') throw new InvalidInputError('Expected a base64 string');
  const clean = value.trim().replace(/=+$/, '');
  if (clean.length === 0) throw new InvalidInputError('Empty base64 string');
  if (clean.length % 4 === 1) throw new InvalidInputError('Malformed base64 string (bad length)');

  const outLength = Math.floor((clean.length * 3) / 4);
  const out = new Uint8Array(outLength);
  let acc = 0;
  let accBits = 0;
  let outIndex = 0;

  for (let i = 0; i < clean.length; i += 1) {
    const char = clean.charAt(i);
    const sextet = BASE64_LOOKUP[char];
    if (sextet === undefined) throw new InvalidInputError('Base64 string contains invalid characters');
    acc = (acc << 6) | sextet;
    accBits += 6;
    if (accBits >= 8) {
      accBits -= 8;
      out[outIndex] = (acc >>> accBits) & 0xff;
      outIndex += 1;
    }
  }

  if (expectedLength !== undefined && out.length !== expectedLength) {
    throw new InvalidInputError(`Expected ${expectedLength} bytes, got ${out.length}`);
  }
  return out;
}

/** UTF-8 encoding without depending on TextEncoder (absent in some test environments). */
export function utf8ToBytes(value: string): Uint8Array {
  if (typeof value !== 'string') throw new InvalidInputError('Expected a string');
  const bytes: number[] = [];
  for (let i = 0; i < value.length; i += 1) {
    let code = value.charCodeAt(i);
    if (code >= 0xd800 && code <= 0xdbff && i + 1 < value.length) {
      const next = value.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        code = ((code - 0xd800) << 10) + (next - 0xdc00) + 0x10000;
        i += 1;
      }
    }
    if (code < 0x80) {
      bytes.push(code);
    } else if (code < 0x800) {
      bytes.push(0xc0 | (code >>> 6), 0x80 | (code & 0x3f));
    } else if (code < 0x10000) {
      bytes.push(0xe0 | (code >>> 12), 0x80 | ((code >>> 6) & 0x3f), 0x80 | (code & 0x3f));
    } else {
      bytes.push(
        0xf0 | (code >>> 18),
        0x80 | ((code >>> 12) & 0x3f),
        0x80 | ((code >>> 6) & 0x3f),
        0x80 | (code & 0x3f),
      );
    }
  }
  return Uint8Array.from(bytes);
}

/** Concatenates byte arrays (no iterator spreading — ES5 target). */
export function concatBytes(...parts: Uint8Array[]): Uint8Array {
  let total = 0;
  for (let i = 0; i < parts.length; i += 1) total += parts[i].length;
  const out = new Uint8Array(total);
  let offset = 0;
  for (let i = 0; i < parts.length; i += 1) {
    out.set(parts[i], offset);
    offset += parts[i].length;
  }
  return out;
}

/**
 * Best-effort wipe of a byte buffer.
 *
 * This is defence in depth, not a guarantee: the JS engine may have copied the buffer
 * (GC compaction, structured clone). It costs nothing and shortens the window during which
 * a heap snapshot would reveal a private key. Strings can never be wiped — which is exactly
 * why key material is kept as `Uint8Array` everywhere except at the export boundary.
 */
export function wipe(bytes: Uint8Array | null | undefined): void {
  if (!bytes) return;
  bytes.fill(0);
}
